/*
<summary>
   This functional component is all about rendering the Add/Edit Tool BOM form.
   Developer: Aashish Singh, Created Date: 07-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik } from 'formik';
import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { IAddEditFormProps } from '../../../models/forms/IForm';
import { IAddToolBOM } from '../../../models/forms/IToolBOM';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import { ToolBOMValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';

interface IProps extends IAddEditFormProps<IAddToolBOM> {
  isLoading: boolean;
}

/**
* This functional component is used to render the Add/Edit Tool BOM form.
* @param props: Component props containing the initial values, submit handler and loading state
*/
const AddEditToolBOMForm = (props: IProps) => {
  return (
    <Modal
      show={true}
      centered
      onHide={() => {}}
      size="lg"
      className="tool-bom-modal"
    >
      <Modal.Header>
        <Modal.Title>
          {props.id === -1 ? (
            <FormattedMessage id="add_tool_bom" />
          ) : (
            <FormattedMessage id="update_tool_bom" />
          )}
          <button
            onClick={props.onModalClosed}
            className="Crossicons"
            disabled={props.isLoading}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={props.initialValues}
        enableReinitialize
        validationSchema={ToolBOMValidateSchema}
        onSubmit={(values) => {
          const finalPayload: IAddToolBOM = {
            MaterialProductId: values.MaterialProductId,
            ToolModel: values.ToolModel,
            MaterialKatashiki1: values.MaterialKatashiki1,
            Category: values.Category,
            MaterialManufacturerCode: values.MaterialManufacturerCode,
            TecmibizCode: values.TecmibizCode,
          };

          if (values.Id && values.Id > 0) {
            finalPayload.Id = values.Id;
          }

          props.submitHandler(finalPayload);
        }}
      >
        {() => (
          <Form className="user-form tool-bom-modal">
            {props.isLoading && <FormLoader loading={props.isLoading} contained />}
            <Modal.Body>
              <div className="row g-3">
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-3">
                    <Label
                      required
                      label="tool_katashiki_model"
                      className="label-style col-form-label"
                    />
                    <Field
                      className="form-control"
                      name="ToolModel"
                      type="text"
                      as={FormikFormInput}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-3">
                    <Label
                      required
                      label="material_product_id"
                      className="label-style col-form-label"
                    />
                    <Field
                      className="form-control"
                      name="MaterialProductId"
                      type="text"
                      as={FormikFormInput}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-3">
                    <Label
                      required={false}
                      label="material_katashiki"
                      className="label-style col-form-label"
                    />
                    <Field
                      className="form-control"
                      name="MaterialKatashiki1"
                      type="text"
                      as={FormikFormInput}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-3">
                    <Label
                      required={false}
                      label="category"
                      className="label-style col-form-label"
                    />
                    <Field
                      className="form-control"
                      name="Category"
                      type="text"
                      as={FormikFormInput}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-3">
                    <Label
                      required={false}
                      label="manufacturer_name"
                      className="label-style col-form-label"
                    />
                    <Field
                      className="form-control"
                      name="MaterialManufacturerCode"
                      type="text"
                      as={FormikFormInput}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-0">
                    <Label
                      required={false}
                      label="tmb_model"
                      className="label-style col-form-label"
                    />
                    <Field
                      className="form-control"
                      name="TecmibizCode"
                      type="text"
                      as={FormikFormInput}
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex justify-content-end w-100">
                <Button
                  variant="light-danger"
                  onClick={props.onModalClosed}
                  disabled={props.isLoading}
                  className="me-2"
                >
                  <FormattedMessage id="cancel" />
                </Button>
                <LoaderButton
                  type="submit"
                  isLoading={props.isLoading}
                  text="button_save"
                />
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddEditToolBOMForm;
